import { QueueWithAssignmentCounts } from 'lib-shared-types-api';
import { memo } from 'react';

import { dashboardApi } from 'apis/dashboard';
import { useDashboardRefetchEffect } from 'utils/dashboard';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';
import { useServerStorage } from 'utils/useServerStorage';

import { QueueDashboardOrganizationMetrics } from './components/organizationMetrics/queueDashboardOrganizationMetrics';
import { QueueDashboardQueue } from './components/queue/queueDashboardQueue';
import { QueueDashboardTable } from './components/table/queueDashboardTable';

type QueueDashboardQueuesProps = {
  queues: readonly QueueWithAssignmentCounts[];
};

export const QueueDashboardQueues = memo(function QueueDashboardQueues({
  queues,
}: QueueDashboardQueuesProps): JSX.Element {
  const { params } = useAppSearchParams('/dashboard/queues');

  const storage = useServerStorage('user');
  const { layout } = storage.data.dashboard ?? {};

  const { metrics, isFetching, refetch } =
    dashboardApi.useReadQueueDashboardMetricsQuery(
      {
        queueIds: queues.map((queue) => queue.id),
      },
      {
        selectFromResult: ({ data, ...rest }) => ({
          ...rest,
          metrics: data?.data.queue_metrics ?? [],
        }),
      },
    );

  useDashboardRefetchEffect({ refetch });

  const showOrganizationMetrics = params.type === 'all' && !params.q;

  const renderQueues = () => (
    <>
      {showOrganizationMetrics && <QueueDashboardOrganizationMetrics />}

      {queues.map((queue) => (
        <QueueDashboardQueue
          key={queue.id}
          queue={queue}
          isSkeleton={isFetching}
          metrics={metrics.find(({ id }) => id === queue.id)?.metrics}
        />
      ))}
    </>
  );

  return layout === 'compact' ? (
    <QueueDashboardTable>{renderQueues()}</QueueDashboardTable>
  ) : (
    renderQueues()
  );
});
